import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Filter, Loader2 } from 'lucide-react'
import { useEffect, useMemo, useState, type ReactNode } from 'react'

import {
  reportsService,
  type SaleReturnReportFilterOptions,
  type SaleReturnReportFilters,
  type SaleReturnReportResult,
} from '../../../services/reports.service'
import { GlobalFilterBar } from '../../ui/GlobalFilterBar'
import { Button } from '../../ui/button'
import { GlobalPaginator } from '../../ui/global-paginator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table'
import { TableSkeleton } from '../../ui/table-skeleton'
import { TabsContent } from '../../ui/tabs'

const DEFAULT_FILTERS: SaleReturnReportFilters = { status: 'all' }
const REPORT_TABLE_HEAD_CLASS =
  'h-auto px-6 py-3 text-xs font-medium uppercase tracking-wider text-table-header-foreground'
const REPORT_TABLE_CELL_CLASS = 'px-6 py-4 text-table-foreground'

export interface AppliedSaleReturnReportContext {
  filters: SaleReturnReportFilters
}

interface DevolucionesTabProps {
  onAppliedContextChange: (context: AppliedSaleReturnReportContext) => void
}

export function DevolucionesTab({
  onAppliedContextChange,
}: DevolucionesTabProps) {
  const [draftFilters, setDraftFilters] =
    useState<SaleReturnReportFilters>(DEFAULT_FILTERS)
  const [appliedFilters, setAppliedFilters] =
    useState<SaleReturnReportFilters>(DEFAULT_FILTERS)
  const [page, setPage] = useState(1)
  const queryFilters = useMemo(
    () => ({ ...appliedFilters, page }),
    [appliedFilters, page],
  )
  const optionsQuery = useQuery({
    queryKey: ['reports', 'sale-returns', 'filter-options'],
    queryFn: () => reportsService.getSaleReturnFilterOptions(),
    staleTime: 5 * 60 * 1000,
  })
  const reportQuery = useQuery({
    queryKey: ['reports', 'sale-returns', queryFilters],
    queryFn: () => reportsService.getSaleReturnReport(queryFilters),
    placeholderData: (previous) => previous,
  })

  useEffect(() => {
    onAppliedContextChange({ filters: appliedFilters })
  }, [appliedFilters, onAppliedContextChange])

  const applyFilters = () => {
    setAppliedFilters(draftFilters)
    setPage(1)
  }

  const clearFilters = () => {
    setDraftFilters(DEFAULT_FILTERS)
    setAppliedFilters(DEFAULT_FILTERS)
    setPage(1)
  }

  return (
    <TabsContent value="devoluciones" className="space-y-5">
      <GlobalFilterBar
        show={['dates']}
        values={{
          startDate: String(draftFilters.date_from ?? ''),
          endDate: String(draftFilters.date_to ?? ''),
        }}
        onChange={(changes) => {
          setDraftFilters((current) => ({
            ...current,
            ...(changes.startDate !== undefined
              ? { date_from: changes.startDate || undefined }
              : {}),
            ...(changes.endDate !== undefined
              ? { date_to: changes.endDate || undefined }
              : {}),
          }))
        }}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters(draftFilters)}
      >
        <OptionsSelect
          label="Sucursal"
          value={draftFilters.sucursal_id}
          options={optionsQuery.data?.sucursals}
          onChange={(value) =>
            setDraftFilters((current) => ({
              ...current,
              sucursal_id: value === 'all' ? undefined : Number(value),
            }))
          }
        />
        <OptionsSelect
          label="Estado"
          value={draftFilters.status ?? 'all'}
          options={optionsQuery.data?.statuses}
          allowAll={false}
          onChange={(value) =>
            setDraftFilters((current) => ({
              ...current,
              status: value as SaleReturnReportFilters['status'],
            }))
          }
        />
        <OptionsSelect
          label="Forma de pago"
          value={draftFilters.payment_method_id}
          options={optionsQuery.data?.payment_methods}
          onChange={(value) =>
            setDraftFilters((current) => ({
              ...current,
              payment_method_id: value === 'all' ? undefined : Number(value),
            }))
          }
        />
        <div className="flex items-end gap-2">
          <Button type="button" onClick={applyFilters}>
            <Filter className="mr-2 h-4 w-4" />
            Aplicar filtros
          </Button>
          {optionsQuery.isLoading && (
            <span className="flex items-center gap-1 pb-2 text-xs text-slate-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Catálogos
            </span>
          )}
        </div>
      </GlobalFilterBar>

      <SaleReturnResults
        result={reportQuery.data}
        isLoading={reportQuery.isLoading}
        isFetching={reportQuery.isFetching}
        error={reportQuery.error}
        onPageChange={setPage}
      />
    </TabsContent>
  )
}

function SaleReturnResults({
  result,
  isLoading,
  isFetching,
  error,
  onPageChange,
}: {
  result?: SaleReturnReportResult
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  onPageChange: (page: number) => void
}) {
  if (isLoading) {
    return (
      <TableSkeleton
        headers={['Ticket', 'Sucursal', 'Usuario', 'Descuento', 'Importe']}
        columns={['text', 'text', 'text', 'text', 'text']}
        rows={10}
      />
    )
  }
  if (error) {
    return (
      <ReportState
        icon={<AlertCircle className="h-6 w-6 text-red-500" />}
        message="No fue posible cargar el reporte. Intenta nuevamente."
      />
    )
  }
  if (!result || result.rows.length === 0) {
    return (
      <ReportState message="No hay devoluciones para los filtros aplicados." />
    )
  }

  return (
    <div className="border-border bg-table-bg text-table-foreground overflow-hidden rounded-lg border shadow-sm">
      <div className="border-border flex items-center justify-between border-b px-6 py-4">
        <div>
          <h3 className="text-table-foreground font-semibold">Resultados</h3>
          <p className="text-muted-foreground text-xs">
            {result.pagination.total} devoluciones filtradas
          </p>
        </div>
        {isFetching && (
          <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
        )}
      </div>
      <div className="overflow-x-auto">
        <Table className="min-w-max">
          <TableHeader className="border-border bg-table-header-bg text-table-header-foreground border-b">
            <TableRow className="hover:bg-transparent">
              <TableHead className={REPORT_TABLE_HEAD_CLASS}>Ticket</TableHead>
              <TableHead className={REPORT_TABLE_HEAD_CLASS}>
                Sucursal
              </TableHead>
              <TableHead className={REPORT_TABLE_HEAD_CLASS}>Usuario</TableHead>
              <TableHead className={`${REPORT_TABLE_HEAD_CLASS} text-right`}>
                Descuento
              </TableHead>
              <TableHead className={`${REPORT_TABLE_HEAD_CLASS} text-right`}>
                Importe
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="[&_tr]:border-border">
            {result.rows.map((row) => (
              <TableRow key={row.ticket} className="hover:bg-table-row-hover">
                <TableCell className={`${REPORT_TABLE_CELL_CLASS} font-medium`}>
                  {row.ticket}
                </TableCell>
                <TableCell className={REPORT_TABLE_CELL_CLASS}>
                  {row.branch}
                </TableCell>
                <TableCell className={REPORT_TABLE_CELL_CLASS}>
                  {row.user}
                </TableCell>
                <TableCell
                  className={`${REPORT_TABLE_CELL_CLASS} text-right tabular-nums`}
                >
                  {formatMoney(row.discount)}
                </TableCell>
                <TableCell
                  className={`${REPORT_TABLE_CELL_CLASS} text-right font-semibold tabular-nums`}
                >
                  {formatMoney(row.amount)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter className="border-border bg-table-header-bg text-table-foreground border-t font-semibold">
            <TableRow className="hover:bg-transparent">
              <TableCell className={`${REPORT_TABLE_CELL_CLASS} font-semibold`}>
                TOTAL GENERAL
              </TableCell>
              <TableCell className={`${REPORT_TABLE_CELL_CLASS} font-semibold`}>
                Registros: {result.totals.record_count}
              </TableCell>
              <TableCell className={REPORT_TABLE_CELL_CLASS} />
              <TableCell
                className={`${REPORT_TABLE_CELL_CLASS} text-right font-semibold tabular-nums`}
              >
                {formatMoney(result.totals.discount)}
              </TableCell>
              <TableCell
                className={`${REPORT_TABLE_CELL_CLASS} text-right font-semibold tabular-nums`}
              >
                {formatMoney(result.totals.amount)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
      <GlobalPaginator
        totalItems={result.pagination.total}
        itemsPerPage={result.pagination.per_page}
        currentPage={result.pagination.current_page}
        from={result.pagination.from ?? 0}
        to={result.pagination.to ?? 0}
        onPageChange={onPageChange}
      />
    </div>
  )
}

function OptionsSelect({
  label,
  value,
  options = [],
  allowAll = true,
  onChange,
}: {
  label: string
  value: unknown
  options?: SaleReturnReportFilterOptions['sucursals']
  allowAll?: boolean
  onChange: (value: string) => void
}) {
  const selected = value === undefined || value === null ? 'all' : String(value)

  return (
    <Field label={label}>
      <Select value={selected} onValueChange={onChange}>
        <SelectTrigger aria-label={label}>
          <SelectValue placeholder="Todos" />
        </SelectTrigger>
        <SelectContent>
          {allowAll && <SelectItem value="all">Todos</SelectItem>}
          {options.map((option) => {
            const optionValue = String(option.id ?? option.value)
            return (
              <SelectItem key={optionValue} value={optionValue}>
                {option.name ?? option.label ?? optionValue}
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
    </Field>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-slate-700">{label}</label>
      {children}
    </div>
  )
}

function ReportState({ icon, message }: { icon?: ReactNode; message: string }) {
  return (
    <div className="flex min-h-52 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
      {icon}
      <span>{message}</span>
    </div>
  )
}

function hasActiveFilters(filters: SaleReturnReportFilters) {
  const keys = new Set([
    ...Object.keys(DEFAULT_FILTERS),
    ...Object.keys(filters),
  ])

  return Array.from(keys).some((key) => filters[key] !== DEFAULT_FILTERS[key])
}

function formatMoney(value: string | number | null | undefined) {
  return Number(value ?? 0).toLocaleString('es-MX', {
    style: 'currency',
    currency: 'MXN',
  })
}
